// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { provideHooks } from 'redial';
import prioritise from 'src/modules/bluebird-prioritise';
import { omit, invoke, compact, concat, map, toString, get } from 'lodash';
import Promise from 'bluebird';

import getSearchResultsHead from '@seek/seek-jobs-seo/build/get-search-results-head';
import {
  persistCriteriaLocally,
  persistCriteriaRemotely,
  clearActiveRefinement
} from 'src/store/search/search';
import {
  getResults,
  moreOptions,
  resultPageLoad,
  jobsSelector
} from 'src/store/results/results';
import { savedJobSearch } from 'src/store/savedJobs/savedJobs';
import { getSerpLmis } from 'src/store/lmis/lmis';
import { SafetyHelmet } from 'src/components/SafetyHelmet/SafetyHelmet';
import SearchResultPage from 'src/components/SearchResultPage/SearchResultPage';
import { saveJobAfterSignin } from 'src/store/savedJobs/savedJobs';
import { updateLastExecutionDate } from 'src/store/saveSearch/saveSearch';

const hooks = {
  fetch: ({
    dispatch,
    state,
    locale,
    path,
    query,
    cookies,
    requestId,
    referrer,
    userAgent
  }) => {
    const { paginationParameters = {} } = state;

    const searchArgs = {
      locale,
      path,
      query,
      paginationParameters,
      cookies,
      requestId,
      userAgent
    };

    const isNZ = localeArg => localeArg === 'NZ';

    if (!ENV.SERVER && state.moreOptions) {
      return dispatch(moreOptions());
    }
    const getResultsPromise = dispatch(getResults(searchArgs, referrer));
    if (isNZ(locale)) {
      return getResultsPromise;
    }
    const getSerpLmisPromise = dispatch(getSerpLmis({ requestId }));

    return ENV.SERVER
      ? prioritise(getResultsPromise, getSerpLmisPromise)
      : Promise.join(getResultsPromise, getSerpLmisPromise);
  },
  defer: ({ dispatch, routeEnter, state }) => {
    if (routeEnter && window.hj) {
      window.hj('trigger', 'search-results-all-browsers');
    }

    return !state.moreOptions && dispatch(persistCriteriaLocally());
  },
  authenticated: ({
    dispatch,
    routeEnter,
    handleIntent,
    query: { savedsearchid }
  }) => {
    if (routeEnter) {
      if (savedsearchid) {
        dispatch(updateLastExecutionDate(savedsearchid));
      }

      return handleIntent('savejob', id => {
        const savedJob = document.querySelectorAll(`[data-job-id="${id}"]`)[0];

        if (savedJob) {
          savedJob.scrollIntoView();
        }

        const jobId = parseInt(id, 10);

        const getJobFromState = ({ results }) => {
          return jobsSelector(results).filter(item => item.id === jobId)[0];
        };

        dispatch(saveJobAfterSignin(jobId, getJobFromState));
      });
    }

    return false;
  },
  mashup: ({ dispatch, state, authenticated, locale }) => {
    return (
      !state.moreOptions &&
      authenticated &&
      Promise.join(
        dispatch(persistCriteriaRemotely(locale)),
        dispatch(savedJobSearch())
      )
    );
  },
  pageLoad: ({ dispatch, state }) => {
    return !state.moreOptions && dispatch(resultPageLoad());
  }
};

function mapStateToProps({ location: urlLocation, results, seo }) {
  const location = results.location;
  const { jobs, tiers } = results.results || {};
  const combinedJobs = compact(concat(jobs, ...map(tiers, 'jobs')));
  const { title, pagination, relatedSearches } = results;
  const { url: currentUrl, locale, pathname: path, query } = urlLocation;
  const searchParams = get(results, 'results.searchParams');

  return {
    path,
    query,
    jobs: combinedJobs,
    relatedSearches,
    currentUrl,
    locale,
    pagination,
    title,
    page: toString(urlLocation.pageNumber),
    metaDescription: seo.metaDescription,
    location,
    searchParams
  };
}

function mapDispatchToProps(dispatch) {
  return {
    clearActiveRefinement: () => dispatch(clearActiveRefinement())
  };
}

type Props = {
  path: string,
  query: SearchParams,
  jobs: Array<SearchResultJob>,
  relatedSearches?: Array<{ keywords: string }>,
  currentUrl?: string,
  locale?: Locale,
  pagination?: GiantPiano,
  title?: string,
  page: string,
  metaDescription: string,
  location: ?SearchLocation,
  searchParams: ?SearchParams,
  clearActiveRefinement?: Function
};

class SearchResults extends Component<Props> {
  componentWillUnmount() {
    invoke(this.props, 'clearActiveRefinement');
  }

  render() {
    return (
      <Fragment>
        <SafetyHelmet
          {...getSearchResultsHead(omit(this.props, 'clearActiveRefinement'))}
          key="head"
        />
        <SearchResultPage key="body" />
      </Fragment>
    );
  }
}

export default provideHooks(hooks)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(SearchResults)
);
